import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "submitted", label: "Submitted" },
  { value: "info_requested", label: "Info requested" },
  { value: "approved", label: "Approved" },
  { value: "approved_with_conditions", label: "Approved w/ conditions" },
  { value: "rejected", label: "Rejected" },
  { value: "draft", label: "Draft (in progress)" },
];

const STATUS_BADGE = {
  draft: "bg-gray-100 text-gray-700",
  submitted: "bg-brand-teal/15 text-brand-teal",
  info_requested: "bg-amber-100 text-amber-800",
  approved: "bg-brand-success/15 text-brand-success",
  approved_with_conditions: "bg-brand-success/15 text-brand-success",
  rejected: "bg-red-100 text-red-800",
};

export default function AdminMembershipApplications() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [includeTest, setIncludeTest] = useState(true);
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [creatingTest, setCreatingTest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ownerMode, setOwnerMode] = useState(false);

  const isAdmin = user && (user.role === "admin" || user.is_owner);

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      params.set("include_test", includeTest ? "true" : "false");
      params.set("include_drafts", includeDrafts ? "true" : "false");
      const { data } = await api.get(`/admin/membership-applications?${params}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [status, includeTest, includeDrafts, isAdmin]);
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/auth/me").then((r) => setOwnerMode(!!r.data?.is_owner)).catch(() => {});
  }, [isAdmin]);

  if (user === null) return <div className="p-10 text-center text-sm text-brand-muted">Loading…</div>;
  if (!isAdmin) {
    return (
      <div data-testid="admin-access-denied" className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif text-3xl mb-3">Admin access required</h2>
        <p className="text-brand-muted text-sm mb-6">This area is for ColourMatch staff only.</p>
        <a href="/projects" className="inline-block text-sm border border-brand-teal px-5 py-2 text-brand-teal hover:bg-brand-teal hover:text-white transition-colors">Back to your projects</a>
      </div>
    );
  }

  const createOwnerTest = async () => {
    setCreatingTest(true);
    try {
      await api.post("/admin/membership-applications/owner-test");
      await load();
    } finally { setCreatingTest(false); }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="admin-membership-applications">
      <header className="mb-4 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">Admin · ColourMatch Pro</div>
          <h1 className="text-2xl font-bold text-brand-navy">Membership Applications</h1>
          <p className="text-sm text-gray-600">Review painter applications, request info, approve, or reject.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/referrals" className="text-xs bg-brand-cream border border-brand-border text-brand-navy px-3 py-2 rounded-full font-semibold hover:bg-white" data-testid="open-admin-referrals">
            Referrals →
          </Link>
          <Link to="/admin/membership/pricing" className="text-xs bg-brand-cream border border-brand-border text-brand-navy px-3 py-2 rounded-full font-semibold hover:bg-white" data-testid="open-pricing-config">
            Pricing config →
          </Link>
          <Link to="/admin/membership/verification-monitor" className="text-xs bg-brand-cream border border-brand-border text-brand-navy px-3 py-2 rounded-full font-semibold hover:bg-white" data-testid="open-verification-monitor">
            Verification monitor →
          </Link>
          {ownerMode && (
            <button
              onClick={createOwnerTest}
              disabled={creatingTest}
              className="text-xs bg-brand-navy text-white px-3 py-2 rounded-full font-semibold hover:bg-brand-navy/90 disabled:opacity-50"
              data-testid="create-owner-test-btn"
            >
              {creatingTest ? "Creating…" : "+ Create test application"}
            </button>
          )}
        </div>
      </header>

      <div className="flex flex-wrap gap-3 items-end mb-4 p-3 bg-brand-cream border border-brand-border rounded-lg">
        <label className="text-xs">
          <div className="font-semibold text-brand-navy mb-1">Status</div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-brand-border rounded px-2 py-1.5" data-testid="filter-status">
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} data-testid="filter-include-test" />
          Include test applications
        </label>
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={includeDrafts} onChange={(e) => setIncludeDrafts(e.target.checked)} data-testid="filter-include-drafts" />
          Include drafts
        </label>
        <div className="ml-auto text-xs text-gray-600" data-testid="results-count">
          {loading ? "Loading…" : `${total} application${total === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="border border-brand-border rounded-xl overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-brand-cream text-xs uppercase tracking-widest text-brand-navy">
            <tr>
              <th className="text-left px-3 py-2">Business</th>
              <th className="text-left px-3 py-2 hidden sm:table-cell">Tier</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">Submitted</th>
              <th className="text-right px-3 py-2">Progress</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="applications-tbody">
            {rows.length === 0 && !loading && (
              <tr><td colSpan="6" className="p-8 text-center text-gray-500 italic" data-testid="applications-empty">No applications match the current filters.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-brand-border hover:bg-brand-cream/40" data-testid={`app-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-semibold text-brand-navy">{r.business?.legal_name || <em className="text-gray-400">Unnamed</em>}</div>
                  <div className="text-xs text-gray-500">{r.business?.suburb} {r.business?.state}</div>
                  {r.is_test && <div className="text-[10px] mt-0.5 inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">TEST</div>}
                </td>
                <td className="px-3 py-2 hidden sm:table-cell text-xs capitalize">{r.tier_selection?.tier_slug || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status] || "bg-gray-100 text-gray-700"}`} data-testid={`app-status-${r.id}`}>
                    {r.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs hidden md:table-cell">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-AU") : "—"}</td>
                <td className="px-3 py-2 text-right text-xs">{r.progress?.percent ?? 0}%</td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/admin/membership-applications/${r.id}`} className="text-brand-teal hover:underline font-semibold text-xs" data-testid={`app-open-${r.id}`}>Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
